import { useEffect, useState } from "react";
import axios, { getCsrfToken } from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";
import QRCode from "react-qr-code";

export default function Settings() {
  const [settings, setSettings] = useState({
    app_name: "ChatFlow Pro",
    support_email: "",
    max_messages_per_day: "1000",
    session_timeout_hours: "24",
    enable_api_access: "true",
    maintenance_mode: "false",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null); // { secret, otpauthUrl }
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaDisableCode, setTwoFaDisableCode] = useState("");
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get("/admin/settings");
        if (Object.keys(res.data).length > 0) {
          setSettings(prev => ({ ...prev, ...res.data }));
        }
      } catch (err) {
        console.error("Failed to fetch settings:", err);
      }
      setLoading(false);
    };
    fetchSettings();
    fetchTwoFaStatus();
  }, []);

  const fetchTwoFaStatus = async () => {
    try {
      const res = await axios.get("/auth/2fa/status");
      setTwoFaEnabled(Boolean(res.data?.enabled));
    } catch (err) {
      // ignore; auth interceptor will handle invalid sessions
    }
  };

  const startTwoFaSetup = async () => {
    setTwoFaBusy(true);
    try {
      const res = await axios.post("/auth/2fa/setup");
      setTwoFaSetup({
        secret: res.data?.secret,
        otpauthUrl: res.data?.otpauthUrl,
      });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to start 2FA setup");
    }
    setTwoFaBusy(false);
  };

  const enableTwoFa = async () => {
    if (!twoFaCode) {
      alert("2FA code is required");
      return;
    }

    setTwoFaBusy(true);
    try {
      await axios.post("/auth/2fa/enable", { code: twoFaCode });
      alert("2FA enabled");
      setTwoFaEnabled(true);
      setTwoFaSetup(null);
      setTwoFaCode("");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to enable 2FA");
    }
    setTwoFaBusy(false);
  };

  const disableTwoFa = async () => {
    if (!twoFaDisableCode) {
      alert("2FA code is required");
      return;
    }

    setTwoFaBusy(true);
    try {
      const csrfToken = await getCsrfToken(true);
      if (!csrfToken) {
        alert("Security token missing. Please refresh and try again.");
        return;
      }

      await axios.post(
        "/auth/2fa/disable",
        { code: twoFaDisableCode },
        {
          headers: {
            "x-csrf-token": csrfToken,
            "x-csrf-stored": csrfToken,
          },
        }
      );
      alert("2FA disabled");
      setTwoFaEnabled(false);
      setTwoFaSetup(null);
      setTwoFaDisableCode("");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to disable 2FA");
    }
    setTwoFaBusy(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put("/admin/settings", settings);
      alert("Settings saved successfully!");
    } catch (err) {
      alert("Failed to save settings");
    }
    setSaving(false);
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleChangePassword = async () => {
    if (!pw.old_password || !pw.new_password) {
      alert("Current password and new password are required");
      return;
    }
    if (pw.new_password !== pw.confirm_password) {
      alert("New password and confirm password do not match");
      return;
    }
    setPwBusy(true);
    try {
      await axios.post("/client/change-password", {
        old_password: pw.old_password,
        new_password: pw.new_password,
      });
      alert("Admin password changed successfully!");
      setPw({ old_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data?.error || "Failed to change password");
    }
    setPwBusy(false);
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarAdmin />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Settings</h1>
            <p className="text-gray-500 mt-1">Configure system preferences</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 md:px-6 py-2 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* General Settings */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">General</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">APP NAME</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={settings.app_name}
                    onChange={(e) => handleChange("app_name", e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">SUPPORT EMAIL</label>
                  <input
                    type="email"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="support@example.com"
                    value={settings.support_email}
                    onChange={(e) => handleChange("support_email", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Limits & Restrictions */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Limits & Restrictions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">MAX MESSAGES PER DAY (per user)</label>
                  <input
                    type="number"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={settings.max_messages_per_day}
                    onChange={(e) => handleChange("max_messages_per_day", e.target.value)}
                  />
                  <p className="text-xs text-gray-400 mt-1 ml-1">Set -1 for unlimited</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">SESSION TIMEOUT (hours)</label>
                  <input
                    type="number"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={settings.session_timeout_hours}
                    onChange={(e) => handleChange("session_timeout_hours", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Change Admin Password */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-2">Change Admin Password</h2>
              <p className="text-gray-500 text-sm mb-4">
                Update the login password for your Admin account.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">CURRENT PASSWORD</label>
                  <input
                    type="password"
                    placeholder="Enter current password"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={pw.old_password}
                    onChange={(e) => setPw(prev => ({ ...prev, old_password: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">NEW PASSWORD</label>
                  <input
                    type="password"
                    placeholder="Enter new password"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={pw.new_password}
                    onChange={(e) => setPw(prev => ({ ...prev, new_password: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">CONFIRM NEW PASSWORD</label>
                  <input
                    type="password"
                    placeholder="Confirm new password"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={pw.confirm_password}
                    onChange={(e) => setPw(prev => ({ ...prev, confirm_password: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleChangePassword}
                  disabled={pwBusy}
                  className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {pwBusy ? "Updating..." : "Update Password"}
                </button>
              </div>
            </div>

            {/* Security (2FA) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-2">Security (2FA)</h2>
              <p className="text-gray-500 text-sm mb-4">
                Protect your admin account with Google Authenticator. New device login will require a 6-digit code.
              </p>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-bold text-gray-800">Google Authenticator</p>
                  <p className="text-sm text-gray-500">
                    Status: {twoFaEnabled ? "Enabled" : "Disabled"}
                  </p>
                </div>

                {!twoFaEnabled ? (
                  <button
                    onClick={startTwoFaSetup}
                    disabled={twoFaBusy}
                    className="bg-blue-600 text-white px-5 py-2 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {twoFaBusy ? "Please wait..." : twoFaSetup ? "Re-generate QR" : "Setup 2FA"}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      className="border p-2 rounded-xl"
                      placeholder="6-digit code"
                      value={twoFaDisableCode}
                      onChange={(e) => setTwoFaDisableCode(e.target.value)}
                    />
                    <button
                      onClick={disableTwoFa}
                      disabled={twoFaBusy}
                      className="bg-red-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 transition disabled:opacity-50"
                    >
                      Disable
                    </button>
                  </div>
                )}
              </div>

              {!twoFaEnabled && twoFaSetup?.otpauthUrl && (
                <div className="mt-6 space-y-4">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 inline-block">
                    <QRCode value={twoFaSetup.otpauthUrl} size={180} />
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-500">MANUAL SECRET</p>
                    <div className="bg-gray-900 text-green-400 p-3 rounded-xl font-mono text-xs break-all">
                      {twoFaSetup.secret}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <input
                      className="flex-1 border p-3 rounded-xl"
                      placeholder="Enter 6-digit code"
                      value={twoFaCode}
                      onChange={(e) => setTwoFaCode(e.target.value)}
                    />
                    <button
                      onClick={enableTwoFa}
                      disabled={twoFaBusy}
                      className="bg-green-600 text-white px-5 py-2 rounded-xl font-bold hover:bg-green-700 transition disabled:opacity-50"
                    >
                      Enable
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Feature Toggles */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Feature Toggles</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">API Access</p>
                    <p className="text-sm text-gray-500">Allow clients to send messages via API</p>
                  </div>
                  <button
                    onClick={() => handleChange("enable_api_access", settings.enable_api_access === "true" ? "false" : "true")}
                    className={`w-14 h-8 rounded-full transition-colors ${
                      settings.enable_api_access === "true" ? "bg-green-500" : "bg-gray-300"
                    }`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      settings.enable_api_access === "true" ? "translate-x-7" : "translate-x-1"
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Maintenance Mode</p>
                    <p className="text-sm text-gray-500">Temporarily disable client access</p>
                  </div>
                  <button
                    onClick={() => handleChange("maintenance_mode", settings.maintenance_mode === "true" ? "false" : "true")}
                    className={`w-14 h-8 rounded-full transition-colors ${
                      settings.maintenance_mode === "true" ? "bg-red-500" : "bg-gray-300"
                    }`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      settings.maintenance_mode === "true" ? "translate-x-7" : "translate-x-1"
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-red-200">
              <h2 className="text-lg font-bold text-red-600 mb-4">⚠️ Danger Zone</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Clear All Message Logs</p>
                    <p className="text-sm text-gray-500">This action cannot be undone</p>
                  </div>
                  <button className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition">
                    Clear Logs
                  </button>
                </div>
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Disconnect All WhatsApp Sessions</p>
                    <p className="text-sm text-gray-500">Force disconnect all active sessions</p>
                  </div>
                  <button className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition">
                    Disconnect All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
